import React, { useState, useEffect, useCallback, JSX } from 'react';
import { Typography } from '@mui/material';

interface CountdownProps {
  toTime: string; // ISO 8601 timestamp
  onExpire?: () => void;
  bookingEnded: boolean;
  getServerTime: () => number; // Funktio joka palauttaa palvelimen ajan
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const Countdown: React.FC<CountdownProps> = ({ toTime, onExpire, bookingEnded, getServerTime }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [expired, setExpired] = useState<boolean>(false);

  // Ajan laskenta (käyttää palvelimen aikaa)
  const calculateTimeLeft = useCallback((): TimeLeft => {
    const now = getServerTime(); // Käytetään palvelimen aikaa Date.now():n sijaan
    const targetTime = new Date(toTime).getTime();
    const difference = targetTime - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / (1000 * 60)) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    return { days, hours, minutes, seconds };
  }, [toTime, getServerTime]);

  // Päivitä aika
  useEffect(() => {
    const updateTimeLeft = () => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (
        newTimeLeft.days === 0 &&
        newTimeLeft.hours === 0 &&
        newTimeLeft.minutes === 0 &&
        newTimeLeft.seconds === 0
      ) {
        setExpired(true);
        if (onExpire) {
          onExpire();
        }
      }
    };

    updateTimeLeft(); // Alkuarvo

    const timer = setInterval(() => {
      updateTimeLeft();
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft, onExpire]);

  if (bookingEnded) {
    return <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Varaaminen päättynyt</Typography>;
  } else if (new Date(toTime) < new Date() || expired) {
    return <Typography variant="h6" color="primary" sx={{ mt: 2 }}>Varaaminen käynnissä</Typography>;
  }

  const timerComponents: JSX.Element[] = [];

  (Object.keys(timeLeft) as (keyof TimeLeft)[]).forEach((interval) => {
    if (timeLeft[interval] > 0 || interval === 'seconds') {
      timerComponents.push(
        <span key={interval}>
          {timeLeft[interval]} {interval}{' '}
        </span>
      );
    }
  });

  return (
    <Typography variant="h6" color="error" sx={{ mt: 2 }}>
      Varaaminen aukeaa: {timerComponents.length ? timerComponents : <span>Expired</span>}
    </Typography>
  );
};

export default Countdown;
